/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { AlertDialog, DialogTrigger, ActionButton, Text } from '@adobe/react-spectrum';
import Sync from '@spectrum-icons/workflow/Sync';
import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import { compareSchemaVersions } from '../../utils/compareSchema';
import { storeConfig as storeConfigQuery } from '../../queries/2.3.5';
import { storeConfig as storeConfigQuery241 } from '../../queries/2.4.1ce';
import { storeConfig as storeConfigQueryWithUid } from '../../queries/2.4.2ce';
import classes from './StoreConfigButton.scss';

const messages = defineMessages({
    buttonLabel: {
        id: 'storeConfigButton.label',
        defaultMessage: 'Update from Store Configuration'
    },
    dialogTitle: {
        id: 'storeConfigButton.dialog.title',
        defaultMessage: 'Update Configuration'
    },
    dialogDescription: {
        id: 'storeConfigButton.dialog.description',
        defaultMessage:
            'Updating the configuration from the Store Configuration may overwrite some of the already configured fields, including:'
    },
    dialogCategoryRootField: {
        id: 'storeConfigButton.dialog.categoryRootField',
        defaultMessage: 'Catalog Root Category Identifier'
    },
    dialogEnableUidSupportField: {
        id: 'storeConfigButton.dialog.enableUidSupportField',
        defaultMessage: 'Enable Catalog UID Support'
    },
    dialogLanguageField: {
        id: 'storeConfigButton.dialog.languageField',
        defaultMessage: 'Language'
    },
    dialogCta: {
        id: 'storeConfigButton.dialog.cta',
        defaultMessage: 'Are you sure you want to proceed?'
    },
    actionCancel: {
        id: 'action.cancel',
        defaultMessage: 'Cancel'
    },
    actionConfirm: {
        id: 'action.confirm',
        defaultMessage: 'Confirm'
    }
});

/**
 * This Button component opens a confirmation dialog, that if confirmed fetches the store configuration of
 * the first configuration returned from the GraphqlSchemaContextProvider. The configuration is then normalized
 * and emitted as detail of the fetch event on the component.
 *
 * The schema of the event details is: { locale: string, root_category: string | number, uid_supported: boolean }
 *
 * If the GraphqlSchemaContextProvider is in an error or loading state, the button will be disabled.
 *
 * @param {object} props - the props passed to the component.
 * @returns
 */
const StoreConfigButton = props => {
    const { onFetch, label, isDisabled = false, ...otherProps } = props;
    const [schemaStatus, , configs] = useGraphqlSchemaContext();
    const { formatMessage } = useIntl();

    const fetchStoreConfig = async () => {
        if (!configs || configs.length === 0) {
            return;
        }

        const config = configs[0];
        const client = config.gqlClient;

        let query = storeConfigQuery;

        if (config.supportUID) {
            // gte 2.4.2
            query = storeConfigQueryWithUid;
        } else if (compareSchemaVersions(config.schemaVersion, '2.4.1ce') >= 0) {
            // gte 2.4.1
            query = storeConfigQuery241;
        }

        const result = await client.query({ query });

        if (!result.errors && result.data) {
            onFetch({
                detail: {
                    locale: result.data.storeConfig.locale,
                    uid_supported: !!config.supportUID,
                    root_category: config.supportUID
                        ? result.data.storeConfig.root_category_uid
                        : String(result.data.storeConfig.root_category_id),
                    enable_wish_lists: result.data.storeConfig.magento_wishlist_general_is_enabled === '1'
                }
            });
        }
    };

    return (
        <DialogTrigger>
            <ActionButton
                isQuiet={true}
                isDisabled={isDisabled || schemaStatus !== 'done'}
                aria-haspopup="dialog"
                {...otherProps}
            >
                <Sync />
                <Text>{label || formatMessage(messages.buttonLabel)}</Text>
            </ActionButton>

            <AlertDialog
                variant="confirmation"
                title={formatMessage(messages.dialogTitle)}
                primaryActionLabel={formatMessage(messages.actionConfirm)}
                cancelLabel={formatMessage(messages.actionCancel)}
                onPrimaryAction={fetchStoreConfig}
            >
                <p>{formatMessage(messages.dialogDescription)}</p>
                <ul className={classes.fieldList}>
                    <li>{formatMessage(messages.dialogCategoryRootField)}</li>
                    <li>{formatMessage(messages.dialogEnableUidSupportField)}</li>
                    <li>{formatMessage(messages.dialogLanguageField)}</li>
                </ul>
                <p>{formatMessage(messages.dialogCta)}</p>
            </AlertDialog>
        </DialogTrigger>
    );
};

StoreConfigButton.propTypes = {
    onFetch: PropTypes.func,
    label: PropTypes.string,
    isDisabled: PropTypes.bool
};

export default StoreConfigButton;
